import React, { useState, useEffect } from 'react';
import { Save, Send, Shield, AlertTriangle, CheckCircle, Mail, Server, Lock, FileText, Bell } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';

const AdminEmail = () => {
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [settings, setSettings] = useState({
    // SMTP Configuration
    smtpHost: '',
    smtpPort: '587',
    smtpSecure: 'tls',
    smtpUser: '',
    smtpPassword: '',
    
    // Sender Info
    fromEmail: '',
    fromName: '',
    replyTo: '',
    
    // CRM URL for email links
    crmUrl: '',
    
    // Anti-Spam Configuration
    enableDkim: false,
    dkimSelector: 'default',
    dkimPrivateKey: '',
    enableSpf: false,
    spfRecord: '',
    enableDmarc: false,
    dmarcPolicy: 'none',
    
    // Rate Limiting
    maxEmailsPerHour: 100,
    maxEmailsPerDay: 1000,
    delayBetweenEmails: 1,
    
    // Templates
    defaultFooter: '',
    unsubscribeUrl: '',
    
    // Daily Alerts Report
    enableDailyAlertReport: false,
    alertReportTime: '08:00',
    
    // Meeting Reminders
    enableMeetingReminders: false,
    reminderTimes: ['1day', '4hours', '5min'], // 1 day, 4 hours, 5 minutes before
    meetingReminderSubject: 'Recordatorio: Reunión programada - {meetingTitle}',
    meetingReminderTemplate: 'Hola {clientName},\n\nRecuerda que tenemos una reunión programada:\n\n📅 Fecha: {meetingDate}\n⏰ Hora: {meetingTime}\n📍 Tipo: {meetingType}\n📝 Tema: {meetingTitle}\n🔗 Link: {meetingLink}\n📍 Ubicación: {meetingLocation}\n\nAtt: {userName}',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const [sendingAlertReport, setSendingAlertReport] = useState(false);

  const loadSettings = async () => {
    try {
      const data = await api.getEmailSettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('Using default email settings');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveEmailSettings(settings);
      toast.success('Configuración de email guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Ingresa un email de prueba');
      return;
    }
    
    setTesting(true);
    try {
      await api.sendTestEmail(testEmail);
      toast.success('Email de prueba enviado');
    } catch (error) {
      toast.error('Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  const handleSendAlertReport = async () => {
    setSendingAlertReport(true);
    try {
      const result = await api.sendDailyAlertReport();
      if (result.success) {
        toast.success(`Reporte enviado a ${result.emailsSent} usuarios`);
      } else {
        toast.warning(result.message || 'No se enviaron reportes');
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al enviar reporte');
    } finally {
      setSendingAlertReport(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Configuración de Email</h1>
        <p className="text-slate-500 mt-2">Configura el servidor SMTP y opciones anti-spam para envío de correos</p>
      </div>

      <div className="space-y-8">
        {/* SMTP Configuration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Server className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Servidor SMTP</h2>
              <p className="text-sm text-slate-500">Configuración del servidor de correo saliente</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Host SMTP
              </label>
              <input
                type="text"
                value={settings.smtpHost}
                onChange={(e) => handleChange('smtpHost', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Puerto
              </label>
              <select
                value={settings.smtpPort}
                onChange={(e) => handleChange('smtpPort', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="25">25 (Sin cifrado)</option>
                <option value="465">465 (SSL)</option>
                <option value="587">587 (TLS) - Recomendado</option>
                <option value="2525">2525 (Alternativo)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Seguridad
              </label>
              <select
                value={settings.smtpSecure}
                onChange={(e) => handleChange('smtpSecure', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="none">Sin cifrado</option>
                <option value="tls">TLS (STARTTLS)</option>
                <option value="ssl">SSL</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Usuario SMTP
              </label>
              <input
                type="text"
                value={settings.smtpUser}
                onChange={(e) => handleChange('smtpUser', e.target.value)}
                placeholder="usuario@tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Contraseña / API Key
              </label>
              <input
                type="password"
                value={settings.smtpPassword}
                onChange={(e) => handleChange('smtpPassword', e.target.value)}
                placeholder="••••••••••••"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Para Gmail, usa una contraseña de aplicación. Para servicios como SendGrid o Mailgun, usa la API Key.
              </p>
            </div>
          </div>

          {/* Popular Providers */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-3">Configuraciones rápidas:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  handleChange('smtpHost', 'smtp.gmail.com');
                  handleChange('smtpPort', '587');
                  handleChange('smtpSecure', 'tls');
                }}
                className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg hover:bg-slate-100"
              >
                Gmail
              </button>
              <button
                onClick={() => {
                  handleChange('smtpHost', 'smtp.sendgrid.net');
                  handleChange('smtpPort', '587');
                  handleChange('smtpSecure', 'tls');
                }}
                className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg hover:bg-slate-100"
              >
                SendGrid
              </button>
              <button
                onClick={() => {
                  handleChange('smtpHost', 'smtp.mailgun.org');
                  handleChange('smtpPort', '587');
                  handleChange('smtpSecure', 'tls');
                }}
                className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg hover:bg-slate-100"
              >
                Mailgun
              </button>
              <button
                onClick={() => {
                  handleChange('smtpHost', 'email-smtp.us-east-1.amazonaws.com');
                  handleChange('smtpPort', '587');
                  handleChange('smtpSecure', 'tls');
                }}
                className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg hover:bg-slate-100"
              >
                Amazon SES
              </button>
            </div>
          </div>
        </section>

        {/* Sender Info */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Información del Remitente</h2>
              <p className="text-sm text-slate-500">Datos que aparecerán en los correos enviados</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email del Remitente
              </label>
              <input
                type="email"
                value={settings.fromEmail}
                onChange={(e) => handleChange('fromEmail', e.target.value)}
                placeholder="noreply@tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nombre del Remitente
              </label>
              <input
                type="text"
                value={settings.fromName}
                onChange={(e) => handleChange('fromName', e.target.value)}
                placeholder="DataCRM"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reply-To (opcional)
              </label>
              <input
                type="email"
                value={settings.replyTo}
                onChange={(e) => handleChange('replyTo', e.target.value)}
                placeholder="soporte@tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              URL del CRM (para links en emails)
            </label>
            <input
              type="url"
              value={settings.crmUrl || ''}
              onChange={(e) => handleChange('crmUrl', e.target.value)}
              placeholder="https://crm.tudominio.com"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="crm-url-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              URL de tu CRM en produccion. Los links "Ver Lead en CRM" en los emails usaran esta URL.
            </p>
          </div>
        </section>

        {/* Anti-Spam Configuration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-100 rounded-lg">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Configuración Anti-Spam</h2>
              <p className="text-sm text-slate-500">Autenticación para evitar que tus correos sean marcados como spam</p>
            </div>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Importante para evitar spam</p>
              <p className="text-amber-700 mt-1">
                Configura SPF, DKIM y DMARC en tu DNS para mejorar la entregabilidad de tus correos.
                Sin estas configuraciones, tus emails pueden ir a la carpeta de spam.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* SPF */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enableSpf}
                  onChange={(e) => handleChange('enableSpf', e.target.checked)}
                  className="w-5 h-5 rounded text-indigo-600"
                />
                <div>
                  <p className="font-medium text-slate-900">SPF (Sender Policy Framework)</p>
                  <p className="text-sm text-slate-500">Autoriza servidores para enviar emails de tu dominio</p>
                </div>
              </label>
              {settings.enableSpf && (
                <div className="mt-4 ml-8">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Registro SPF (agregar a DNS TXT)
                  </label>
                  <input
                    type="text"
                    value={settings.spfRecord}
                    onChange={(e) => handleChange('spfRecord', e.target.value)}
                    placeholder="v=spf1 include:_spf.google.com ~all"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg font-mono text-sm"
                  />
                </div>
              )}
            </div>

            {/* DKIM */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enableDkim}
                  onChange={(e) => handleChange('enableDkim', e.target.checked)}
                  className="w-5 h-5 rounded text-indigo-600"
                />
                <div>
                  <p className="font-medium text-slate-900">DKIM (DomainKeys Identified Mail)</p>
                  <p className="text-sm text-slate-500">Firma digital que verifica que el email no fue alterado</p>
                </div>
              </label>
              {settings.enableDkim && (
                <div className="mt-4 ml-8 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Selector DKIM
                    </label>
                    <input
                      type="text"
                      value={settings.dkimSelector}
                      onChange={(e) => handleChange('dkimSelector', e.target.value)}
                      placeholder="default"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Clave Privada DKIM
                    </label>
                    <textarea
                      value={settings.dkimPrivateKey}
                      onChange={(e) => handleChange('dkimPrivateKey', e.target.value)}
                      placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                      rows={4}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg font-mono text-xs"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* DMARC */}
            <div className="p-4 border border-slate-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enableDmarc}
                  onChange={(e) => handleChange('enableDmarc', e.target.checked)}
                  className="w-5 h-5 rounded text-indigo-600"
                />
                <div>
                  <p className="font-medium text-slate-900">DMARC (Domain-based Message Authentication)</p>
                  <p className="text-sm text-slate-500">Política que indica qué hacer con emails que fallan SPF/DKIM</p>
                </div>
              </label>
              {settings.enableDmarc && (
                <div className="mt-4 ml-8">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Política DMARC
                  </label>
                  <select
                    value={settings.dmarcPolicy}
                    onChange={(e) => handleChange('dmarcPolicy', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="none">none - Solo monitoreo (recomendado iniciar aquí)</option>
                    <option value="quarantine">quarantine - Enviar a spam si falla</option>
                    <option value="reject">reject - Rechazar si falla</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Rate Limiting */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Lock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Límites de Envío</h2>
              <p className="text-sm text-slate-500">Controla la cantidad de emails para evitar bloqueos</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Máx. emails por hora
              </label>
              <input
                type="number"
                value={settings.maxEmailsPerHour}
                onChange={(e) => handleChange('maxEmailsPerHour', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Máx. emails por día
              </label>
              <input
                type="number"
                value={settings.maxEmailsPerDay}
                onChange={(e) => handleChange('maxEmailsPerDay', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Delay entre emails (seg)
              </label>
              <input
                type="number"
                value={settings.delayBetweenEmails}
                onChange={(e) => handleChange('delayBetweenEmails', parseInt(e.target.value))}
                min="0"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
            </div>
          </div>
        </section>

        {/* Daily Alert Report */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Bell className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Reporte Diario de Alertas</h2>
              <p className="text-sm text-slate-500">Envía un resumen de alertas pendientes cada mañana</p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Activar Reporte Diario de Alertas</p>
                <p className="text-sm text-slate-500">Cada usuario recibirá sus alertas pendientes en la mañana</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableDailyAlertReport}
                onChange={(e) => handleChange('enableDailyAlertReport', e.target.checked)}
                className="w-5 h-5 rounded text-orange-600"
              />
            </label>

            {settings.enableDailyAlertReport && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Hora de envío automático
                  </label>
                  <input
                    type="time"
                    value={settings.alertReportTime}
                    onChange={(e) => handleChange('alertReportTime', e.target.value)}
                    className="w-32 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div className="border-t border-orange-200 pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Envío Manual</p>
                  <p className="text-xs text-slate-500 mb-3">
                    Envía el reporte de alertas a todos los usuarios ahora mismo. Admin recibe todas las alertas, comerciales solo las suyas.
                  </p>
                  <button
                    onClick={handleSendAlertReport}
                    disabled={sendingAlertReport}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingAlertReport ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar Reporte Ahora
                      </>
                    )}
                  </button>
                </div>
                
                <p className="text-xs text-slate-500">
                  El reporte solo se envía si hay alertas pendientes. Si no hay alertas, no se envía correo.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Meeting Reminders */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Recordatorios de Reuniones</h2>
              <p className="text-sm text-slate-500">Envía recordatorios automáticos antes de cada reunión</p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Activar Recordatorios de Reuniones</p>
                <p className="text-sm text-slate-500">Envía emails automáticos recordando las reuniones agendadas</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableMeetingReminders}
                onChange={(e) => handleChange('enableMeetingReminders', e.target.checked)}
                className="w-5 h-5 rounded text-blue-600"
              />
            </label>

            {settings.enableMeetingReminders && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                {/* Reminder Times */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Cuándo enviar recordatorios
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: '1day', label: '1 día antes' },
                      { value: '4hours', label: '4 horas antes' },
                      { value: '1hour', label: '1 hora antes' },
                      { value: '30min', label: '30 min antes' },
                      { value: '5min', label: '5 min antes' },
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-blue-400">
                        <input
                          type="checkbox"
                          checked={settings.reminderTimes?.includes(option.value)}
                          onChange={(e) => {
                            const current = settings.reminderTimes || [];
                            if (e.target.checked) {
                              handleChange('reminderTimes', [...current, option.value]);
                            } else {
                              handleChange('reminderTimes', current.filter(t => t !== option.value));
                            }
                          }}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <span className="text-sm text-slate-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Asunto del email
                  </label>
                  <input
                    type="text"
                    value={settings.meetingReminderSubject}
                    onChange={(e) => handleChange('meetingReminderSubject', e.target.value)}
                    placeholder="Recordatorio: Reunión programada"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Message Template */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Plantilla del mensaje
                  </label>
                  <textarea
                    value={settings.meetingReminderTemplate}
                    onChange={(e) => handleChange('meetingReminderTemplate', e.target.value)}
                    rows={8}
                    placeholder="Hola {clientName}..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Variables disponibles: {'{clientName}'}, {'{meetingDate}'}, {'{meetingTime}'}, {'{meetingType}'}, {'{meetingTitle}'}, {'{userName}'}, {'{meetingLocation}'}, {'{meetingLink}'}
                  </p>
                </div>

                {/* Preview */}
                <div className="border-t border-blue-200 pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Vista previa</p>
                  <div className="p-4 bg-white rounded-lg border border-slate-200 text-sm whitespace-pre-wrap">
                    {(settings.meetingReminderTemplate || '')
                      .replace('{clientName}', 'María García')
                      .replace('{meetingDate}', '15 de febrero, 2026')
                      .replace('{meetingTime}', '10:00 AM')
                      .replace('{meetingType}', 'Virtual')
                      .replace('{meetingTitle}', 'Presentación de propuesta')
                      .replace('{userName}', 'Carlos Rodríguez')
                      .replace('{meetingLocation}', 'Oficina principal')
                      .replace('{meetingLink}', 'https://meet.google.com/xxx-xxx-xxx')
                    }
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Test Email */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Send className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Probar Configuración</h2>
              <p className="text-sm text-slate-500">Envía un email de prueba para verificar la configuración</p>
            </div>
          </div>

          <div className="flex gap-4">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tu@email.com"
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleTestEmail}
              disabled={testing}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {testing ? 'Enviando...' : 'Enviar Prueba'}
            </button>
          </div>
        </section>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEmail;
